<script setup>
import { reactive, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { CLOUD_STORAGE } from "../../../core/constants.js";
import CloudStorageService from "../../services/cloudStorageService.js";
import { useDocumentUpload } from "../../composables/useDocumentUpload.js";

const props = defineProps({
  // { id, name, provider }
  storage: { type: Object, required: true },
  // null/undefined = unfiled ("Documents"), matching the local-file upload path
  projectId: { type: String, default: null },
});
const emit = defineEmits(["close", "rename", "remove", "uploaded"]);

const cloudStorageService = new CloudStorageService();
const { uploadCloudDocuments } = useDocumentUpload();

const EXPORTABLE_STATUS = { EXCEEDS_MAXIMUM_SIZE: "EXCEEDS_MAXIMUM_SIZE" };

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

// ─── Folder navigation stack ────────────────────────────────────────────────
// Each visited folder keeps its own paged generator + accumulated items, so
// going back doesn't lose scroll position or re-fetch what's already loaded.
const viewMode = ref(localStorage.getItem("cloud-finder-view-style") || "list");
watch(viewMode, (val) => localStorage.setItem("cloud-finder-view-style", val));

const state = reactive({
  selectedFiles: new Set(),
  folderItems: [],
  fetching: false,
  uploading: false,
});

class FinderStack {
  #back = [];
  #forward = [];

  get canBack() { return this.#back.length > 1; }
  get canForward() { return this.#forward.length > 0; }
  get currentFolder() { return this.#back[this.#back.length - 1]; }

  get path() {
    const path = this.#back
      .map((item, index, array) => ({ backSteps: array.length - index - 1, name: item.name }))
      .filter((folder) => !!folder.name);
    if (path.length > 4) path.splice(1, path.length - 3, { backSteps: 0, name: "…" });
    return path;
  }

  goBack(times = 1) {
    state.selectedFiles.clear();
    for (let i = 0; i < times; i++) this.#forward.push(this.#back.pop());
    this.#recalc();
  }

  goForward() {
    state.selectedFiles.clear();
    this.#back.push(this.#forward.shift());
    this.#recalc();
  }

  goHome() {
    state.selectedFiles.clear();
    this.#forward = this.#back.splice(1).concat(this.#forward);
    this.#recalc();
  }

  push(id, name, generator) {
    this.#forward = [];
    this.#back.push({ id, name, items: [], generator });
    this.#recalc();
    return this.currentFolder;
  }

  fillFolder(folderId, chunk) {
    const folder = this.#back.find((f) => f.id === folderId);
    if (!folder) return;
    folder.items = folder.items.concat(chunk.map((item) => ({
      ...item,
      isFolder: item.__typename === "CloudStorageFolder" || item.mimeType === "application/vnd.google-apps.shortcut",
      isNotAccessible: item.exportableStatus === EXPORTABLE_STATUS.EXCEEDS_MAXIMUM_SIZE,
    })));
    this.#recalc();
  }

  getFolderById(id) { return this.#back.find((f) => f.id === id); }

  #recalc() { state.folderItems = this.currentFolder?.items ?? []; }
}
const finderStack = new FinderStack();

function getItems(parentFolderId, parentFolderName) {
  let folder = finderStack.getFolderById(parentFolderId);
  if (!folder) {
    const generator = cloudStorageService.folderItems(props.storage.id, parentFolderId);
    folder = finderStack.push(parentFolderId, parentFolderName, generator);
  }
  state.fetching = true;
  folder.generator.next()
    .then((chunk) => { if (chunk.value) finderStack.fillFolder(parentFolderId, chunk.value); })
    .catch((error) => console.error("Error fetching drive items:", error))
    .finally(() => { state.fetching = false; });
}

const listContainerRef = ref(null);
const gridContainerRef = ref(null);
function scrollHandler(e) {
  const { scrollHeight, scrollTop, clientHeight } = e.target;
  if (Math.round(scrollHeight) - Math.round(scrollTop + clientHeight) < 2) {
    getItems(finderStack.currentFolder.id, finderStack.currentFolder.name);
  }
}

onMounted(() => getItems(null, ""));

function itemOnClick(item) {
  if (item.isNotAccessible) return;
  if (item.isFolder) {
    state.selectedFiles.clear();
    getItems(item.id, item.name);
  } else if (state.selectedFiles.has(item)) {
    state.selectedFiles.delete(item);
  } else {
    state.selectedFiles.add(item);
  }
}

function startUpload() {
  const files = Array.from(state.selectedFiles).map((f) => ({ id: f.id, name: f.name }));
  state.uploading = true;
  uploadCloudDocuments(props.projectId, props.storage.id, files);
  state.uploading = false;
  state.selectedFiles.clear();
  // Close the whole Add Document popup (not just this finder) — the upload
  // itself continues in the background via the global upload monitor, same
  // as the local-file path (AddModal.vue's onFilesPicked() does the same).
  emit("uploaded");
}

const showMenu = ref(false);
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[450] flex items-center justify-center p-4" @click.self="close" @click="showMenu = false">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[720px] flex flex-col overflow-hidden" @click.stop>

      <!-- Header -->
      <div class="flex items-center gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 shrink-0">
          <svg v-if="storage.provider === CLOUD_STORAGE.GOOGLE_DRIVE" viewBox="0 0 24 21" width="20" height="18">
            <path d="M7.5 13.5H22l-3.5 6H4L7.5 13.5z" fill="#34A853" />
            <path d="M4 19.5L7.5 13.5 12 6 16.5 13.5H7.5L4 19.5z" fill="#4285F4" />
            <path d="M12 6l4.5 7.5H22L17.5 6H12z" fill="#FBBC05" />
            <path d="M7.5 13.5l-3.5 6L0 14 4.5 7 7.5 13.5z" fill="#EA4335" />
          </svg>
          <svg v-else-if="storage.provider === CLOUD_STORAGE.ONE_DRIVE" viewBox="0 0 24 18" width="22" height="16" fill="none">
            <path d="M14 12H5.5a3.5 3.5 0 0 1 0-7h.1A5 5 0 0 1 14.9 3 4 4 0 0 1 19 7v.5" stroke="#0078D4" stroke-width="1.5" stroke-linecap="round" />
            <path d="M8 14.5A3.5 3.5 0 0 0 11.5 18h7a3.5 3.5 0 0 0 0-7H11.5A3.5 3.5 0 0 0 8 14.5z" fill="#0078D4" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18">
            <path d="M6 8.5A3.5 3.5 0 0 1 9.5 5h1.6a3.5 3.5 0 0 1 2.68 1.25l.44.53a1.5 1.5 0 0 0 1.15.54H16.5A3.5 3.5 0 0 1 20 10.82v5.68A3.5 3.5 0 0 1 16.5 20h-9A3.5 3.5 0 0 1 4 16.5v-6A2 2 0 0 1 6 8.5z" fill="#0061D5" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-bold text-slate-800 dark:text-slate-100 m-0 truncate">{{ storage.name }}</h2>
          <p class="text-xs text-slate-400 dark:text-slate-500 m-0">Select documents to add. Maximum file size: 200MB</p>
        </div>
        <div class="relative shrink-0">
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
            @click.stop="showMenu = !showMenu"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          <div
            v-if="showMenu"
            class="absolute top-[calc(100%+4px)] right-0 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-10 overflow-hidden"
            @click.stop
          >
            <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="showMenu = false; emit('rename')">
              Rename
            </button>
            <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="showMenu = false; emit('remove')">
              Remove
            </button>
          </div>
        </div>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors shrink-0"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- Navigator -->
      <div class="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div class="flex items-center gap-1 min-w-0 flex-1">
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border-0 bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-default shrink-0"
            :disabled="!finderStack.canBack"
            @click="finderStack.goBack()"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border-0 bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-default shrink-0"
            :disabled="!finderStack.canForward"
            @click="finderStack.goForward()"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <div v-if="finderStack.path.length" class="flex items-center gap-1 min-w-0 overflow-hidden ml-1">
            <button
              v-for="folder in finderStack.path"
              :key="folder.name + folder.backSteps"
              class="max-w-[130px] truncate text-sm border-0 bg-transparent cursor-pointer px-1 py-1 rounded"
              :class="folder.backSteps === 0
                ? 'text-slate-800 dark:text-slate-100 font-semibold cursor-default'
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'"
              @click="folder.backSteps > 0 && finderStack.goBack(folder.backSteps)"
            >
              {{ folder.name }}
            </button>
          </div>
        </div>

        <!-- View mode toggle -->
        <div class="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5 shrink-0">
          <button
            :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
              viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
            title="List view"
            @click="viewMode = 'list'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
          </button>
          <button
            :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
              viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
            title="Grid view"
            @click="viewMode = 'grid'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="13" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="3" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="13" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="relative min-h-[360px] max-h-[360px] bg-slate-50 dark:bg-slate-950/40">
        <div v-if="state.fetching && !state.folderItems.length" class="absolute inset-0 flex items-center justify-center">
          <div class="w-6 h-6 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
        </div>

        <div v-else-if="!state.folderItems.length" class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
          <p class="text-sm m-0">This folder is empty</p>
        </div>

        <!-- List view -->
        <div v-else-if="viewMode === 'list'" ref="listContainerRef" class="h-[360px] overflow-y-auto" @scroll="scrollHandler">
          <div
            v-for="item in state.folderItems"
            :key="item.id"
            class="flex items-center gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 cursor-pointer transition-colors"
            :class="item.isNotAccessible
              ? 'opacity-50 cursor-not-allowed'
              : (state.selectedFiles.has(item) ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-white dark:hover:bg-slate-800/60')"
            @click="itemOnClick(item)"
          >
            <svg v-if="item.isFolder" width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-slate-400 shrink-0"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-slate-400 shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-slate-700 dark:text-slate-200 m-0 truncate">{{ item.name }}</p>
              <p v-if="item.exportableStatus === EXPORTABLE_STATUS.EXCEEDS_MAXIMUM_SIZE" class="text-xs italic text-slate-400 dark:text-slate-500 m-0">File exceeds maximum size allowed</p>
            </div>
            <svg v-if="state.selectedFiles.has(item)" width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-blue-500 shrink-0"><circle cx="12" cy="12" r="10" fill="currentColor" /><path d="M7 12.5l3 3 7-7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </div>
        </div>

        <!-- Grid view -->
        <div v-else ref="gridContainerRef" class="h-[360px] overflow-y-auto p-4 flex flex-wrap gap-3 content-start" @scroll="scrollHandler">
          <div
            v-for="item in state.folderItems"
            :key="item.id"
            class="w-[110px] flex flex-col items-center gap-2 p-2 rounded-xl cursor-pointer transition-colors"
            :class="item.isNotAccessible ? 'opacity-50 cursor-not-allowed' : (state.selectedFiles.has(item) ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-white dark:hover:bg-slate-800/60')"
            @click="itemOnClick(item)"
          >
            <svg v-if="item.isFolder" width="40" height="40" viewBox="0 0 24 24" fill="none" class="text-slate-300 dark:text-slate-600 shrink-0"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
            <svg v-else width="34" height="34" viewBox="0 0 24 24" fill="none" class="text-slate-300 dark:text-slate-600 shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <p class="text-xs text-slate-600 dark:text-slate-300 m-0 text-center truncate w-full">{{ item.name }}</p>
          </div>
        </div>

        <div v-if="state.fetching && state.folderItems.length" class="absolute bottom-2 left-1/2 -translate-x-1/2">
          <div class="w-5 h-5 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
        <button
          class="px-5 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
        <button
          :disabled="state.selectedFiles.size === 0 || state.uploading"
          class="px-5 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
          @click="startUpload"
        >
          Upload{{ state.selectedFiles.size ? ` (${state.selectedFiles.size})` : "" }}
        </button>
      </div>
    </div>
  </div>
</template>
